import React, {
  ComponentType,
  lazy,
} from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
} from 'react-router-dom';
import FullScreenSpinner from 'core/components/FullScreenSpinner';
import ProtectedRouter from 'pages/Auth/ProtectedRouter';
import AuthorizeScreen from 'pages/Auth';
import SessionEndedScreen from 'pages/Auth/SessionEnded';
import legacyRoutes from 'pages/Dashboard/routes';
import routes from 'views/EMR/routes';
import LazyLoadComponent from 'core/components/LazyLoadComponent';
import SupportPortal from 'views/EMR/PopulateSupport/Portal';
import SupportLayout from 'views/EMR/PopulateSupport/SupportLayout';
import SupportProtectedLayout from 'views/EMR/PopulateSupport/SupportProtectedLayout';
import SupportAuthGuard from 'views/EMR/PopulateSupport/SupportAuthGuard';
import LoginSection from 'views/EMR/PopulateSupport/LoginSection';
import ErrorBoundaryWithAuth from 'pages/Dashboard/components/ErrorBoundary/ErrorBoundaryWithAuth';
import useEMRAccess from 'views/EMR/hooks/useEMRAccess';

// Vite cannot resolve bare-specifier dynamic imports (e.g. `pages/Dashboard/pages/Billing`)
// at runtime in the browser. import.meta.glob() tells Vite to scan these patterns at build
// time and produce a module map that the browser can actually look up by key.
type LazyModule = () => Promise<{ default: ComponentType }>;

const dashboardPageModules = import.meta.glob<{ default: ComponentType }>(
  '/src/pages/Dashboard/pages/*/index.tsx',
);
const emrPageDirModules = import.meta.glob<{ default: ComponentType }>(
  '/src/views/EMR/*/index.tsx',
);
const emrPageFileModules = import.meta.glob<{ default: ComponentType }>(
  [
    '/src/views/EMR/*.tsx',
    '!/src/views/EMR/*.spec.tsx',
  ],
);

function ErrorBoundaryLayout() {
  return (
    <ErrorBoundaryWithAuth>
      <Outlet />
    </ErrorBoundaryWithAuth>
  );
}

function AppRouteRedirect() {
  const {
    defaultPath,
    userInfo,
  } = useEMRAccess();

  return userInfo === null
    ? <FullScreenSpinner />
    : <Navigate to={defaultPath} replace />;
}

function EmrAccessBoundary() {
  const location = useLocation();
  const {
    canPath,
    getAccessiblePath,
    userInfo,
  } = useEMRAccess();

  if (userInfo === null) {
    return <FullScreenSpinner />;
  }

  if (!canPath(location.pathname)) {
    return <Navigate to={getAccessiblePath(location.pathname)} replace />;
  }

  return <Outlet />;
}

export const APP_ROUTES = {
  EMR: {
    ROOT: '/',
    AUTHORIZE: '/authorize',
    SESSION_ENDED: '/session-ended',
  },
  POPULATE_SUPPORT: {
    ROOT: '/support',
    PORTAL: '/support/portal',
  },
};

export default createBrowserRouter([{
  element: <ErrorBoundaryLayout />,
  children: [{
    path: APP_ROUTES.EMR.ROOT,
    element: (
      <ProtectedRouter>
        <LazyLoadComponent
          Component={lazy(() => import('views/EMR/Layout'))}
        />
      </ProtectedRouter>
    ),
    children: [{
      element: <EmrAccessBoundary />,
      children: [
        ...legacyRoutes.map((route) => ({
          path: `/${route?.href}`,
          element: (
            <LazyLoadComponent
              Component={lazy(
                dashboardPageModules[`/src/pages/Dashboard/pages/${route?.name}/index.tsx`] as LazyModule,
              )}
            />
          ),
          ...(route?.subRoutes ? { children: route.subRoutes } : {}),
        })),
        ...routes.map((route) => ({
          path: `/${route?.href}`,
          element: (
            <LazyLoadComponent
              Component={lazy(
                (emrPageDirModules[`/src/views/EMR/${route?.name}/index.tsx`]
                  ?? emrPageFileModules[`/src/views/EMR/${route?.name}.tsx`]) as LazyModule,
              )}
            />
          ),
          ...(route?.subRoutes ? { children: route.subRoutes } : {}),
        })), {
          path: '/',
          element: <AppRouteRedirect />,
        }, {
          path: '*',
          element: <AppRouteRedirect />,
        },
      ],
    }],
  }, {
    path: APP_ROUTES.EMR.AUTHORIZE,
    element: <AuthorizeScreen />,
  }, {
    path: APP_ROUTES.EMR.SESSION_ENDED,
    element: <SessionEndedScreen />,
  }, {
    path: APP_ROUTES.POPULATE_SUPPORT.ROOT,
    element: <SupportLayout />,
    children: [{
      index: true,
      element: (
        <SupportAuthGuard>
          <LoginSection />
        </SupportAuthGuard>
      ),
    }, {
      element: <SupportProtectedLayout />,
      children: [{
        path: APP_ROUTES.POPULATE_SUPPORT.PORTAL,
        element: <SupportPortal />,
      }],
    }],
  }, {
    path: '*',
    element: <Navigate to={APP_ROUTES.EMR.ROOT} />,
  }],
}]);
